import React, { useCallback, useEffect, useState, Fragment, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon, BookOpenIcon, ClockIcon, TrendingUpIcon, GlobeIcon, ShieldCheckIcon, HeartIcon, UsersIcon, CheckCircleIcon, BriefcaseIcon, DollarSignIcon, ChevronDownIcon, ChevronUpIcon, CrossIcon } from 'lucide-react';
import { degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { getGuideBySlug } from '../data/guides';
import { getStaticGuideListings, listingDisclosure } from '../data/guideListings';
import { UniversityListingCard } from '../components/UniversityListingCard';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { usePageMeta } from '../hooks/usePageMeta';
const benefitIconMap: Record<string, React.ElementType> = {
  ClockIcon,
  TrendingUpIcon,
  GlobeIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  HeartIcon,
  UsersIcon,
  CheckCircleIcon
};
export function GuidePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const guide = slug ? getGuideBySlug(slug) : undefined;
  usePageMeta(guide?.metaTitle || 'Guide | OnlineCollegeGuide', guide?.metaDescription || 'Explore accredited online colleges and degree programs.', {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'article'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    areaOfStudy: guide?.focusValue || ''
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  // Some guides carry a fixed editorial line-up instead of live API results.
  const staticListings = getStaticGuideListings(slug);
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    // Curated guides never query the feed; their line-up is fixed.
    if (staticListings) return;
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, [staticListings]);
  // Auto-fetch on page load with the guide's focus value
  useEffect(() => {
    if (staticListings) return;
    if (guide?.focusValue) {
      fetchResults('', guide.focusValue, guide.defaultSpecialization || '');
    }
  }, [guide?.focusValue, guide?.defaultSpecialization, fetchResults, staticListings]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  if (!guide) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-1839" data-mp="qkgk8szfhw4">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1840" data-mp="5pf1bgaifgiv">Guide Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1841" data-mp="aiq7rgbetva9">
          We couldn't find the guide you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1842" data-mp="fjdx7xjzq42d">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-1843" data-mp="3j04a8wdzphdk">
      {/* Page Header */}
      <div className="hero-bg text-white py-6 sm:py-8" data-id="element-1844" data-mp="3nvjkzfgpax5r">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1845" data-mp="3rz7rrvxidopy">
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-gray-300 mb-3" aria-label="Breadcrumb" data-id="element-1846" data-mp="137f0aqyegsae">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-1847" data-mp="177fpn6n1enw8">
              <li className="inline-flex items-center" data-id="element-1848" data-mp="2rr1bswu5lhnz">
                <Link to="/" className="inline-flex items-center hover:text-white transition-colors" data-id="element-1849" data-mp="2rpqbj6zx2s1m">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-1850" data-mp="2ixehzc9iaohf" />
                  Home
                </Link>
              </li>
              <li data-id="element-1851" data-mp="2dybqkumg8grx">
                <div className="flex items-center" data-id="element-1852" data-mp="2dyuwte2qlgq4">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1853" data-mp="290joj7s3bea0" />
                  <span className="ml-1 md:ml-2" data-id="element-1854" data-mp="28xnondwomfix">Guides</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-1855" data-mp="2439ybpwvmyl4">
                <div className="flex items-center" data-id="element-1856" data-mp="1zyk5yb1fg24y">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1857" data-mp="2016mv1a7jtzu" />
                  <span className="ml-1 md:ml-2 text-white font-medium" data-id="element-1858" data-mp="1v1k82neqe5ox">
                    {guide.breadcrumbLabel}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          {guide.heroEyebrow && <div className="hidden sm:inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-semibold mb-3 text-gold-light uppercase tracking-widest" data-id="element-1859" data-mp="cdisqdshx74q">
              <CrossIcon className="w-3.5 h-3.5 mr-2" data-id="element-1860" data-mp="h6218wcbtc2o" />
              {guide.heroEyebrow}
            </div>}

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 text-white" data-id="element-1861" data-mp="8bnob0f9qdmq">
            {guide.heroHeadline}
          </h1>
          {/* Hidden on mobile so the first listing lands above the fold. */}
          <p className="hidden sm:block text-base text-gray-300 max-w-3xl" data-id="element-1862" data-mp="103d38opcejlt">
            {guide.heroDescription}
          </p>
        </div>
      </div>

      {/* Desktop keeps the trust band directly under the hero; on mobile it
          would push the first listing below the fold, so it moves after the
          listings instead. */}
      <div className="hidden lg:block" data-id="element-1863" data-mp="36ryhcouhdl6z">
        <TrustSignals data-id="element-1864" data-mp="3bsco83kcip3f" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-1865" data-mp="3btnuca19k8xp">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1866" data-mp="348lizet3ae6z">
          {/* Main Content - Listings */}
          <div className="lg:w-3/4 order-1" data-id="element-1867" data-mp="2zajtoc52lin2">
            {/* Curated listings. Editorially written and always shown in the
                same order, so they bypass the API states entirely. */}
            {staticListings && <div data-id="element-1868" data-mp="38b85d06zr6ws">
                <div className="space-y-5" data-id="element-1869" data-mp="388lxt0k3orek">
                  {staticListings.map(listing => <UniversityListingCard key={listing.id} listing={listing} data-id="element-1870" data-mp="2kj1euhny5u5a" />)}
                </div>
                <p className="mt-6 text-xs text-gray-500 leading-relaxed" data-id="element-1871" data-mp="2uciazsbk3lc4">
                  {listingDisclosure}
                </p>
              </div>}

            {/* Loading State */}
            {!staticListings && isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-1872" data-mp="1wvtca7hwkz6y">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1873" data-mp="1c9pv6declm4z" />
                <p className="text-gray-600 font-medium" data-id="element-1874" data-mp="1cbsuxp5aytmw">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!staticListings && !isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1875" data-mp="1qais14s4gtb9">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1876" data-mp="1v7j01l8j8650" />
                <div data-id="element-1877" data-mp="1z9dwocfipvcg">
                  <p className="text-red-800 font-medium" data-id="element-1878" data-mp="1z8uohnfo6lbm">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-1879" data-mp="240m7ncwhxorq">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!staticListings && !isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1880" data-mp="2iyobzpbcqea1">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1881" data-mp="7ce3loh24r54" data-mpi={idx}>
                    <MatchedSchoolCard school={school} index={idx} data-id="element-1882" data-mp="3ti9n0gur5y8o" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1883" data-mp="q119g9725kjr" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!staticListings && !isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1884" data-mp="bbmxctc3kp0o">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1885" data-mp="18styxtmx4nmq">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1886" data-mp="13ziv81dekmz1" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1887" data-mp="2bcu9qyep7ze9">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1888" data-mp="27d3byaqr8j08">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/4 order-2" data-id="element-1889" data-mp="2u93kbbqe67ti">
            <div className="sticky top-24 space-y-6" data-id="element-1890" data-mp="2qaea1s0nh6e3">
              {/* Lead Form */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden" data-id="element-1891" data-mp="341j48el916h1">
                <div className="bg-navy px-6 py-4" data-id="element-1892" data-mp="2z1emymcio7pr">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-1893" data-mp="2z2ojh73q17ua">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1894" data-mp="3915hgo12umpu">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-1895" data-mp="htp0f9er5nz">
                  <div data-id="element-1896" data-mp="fr2443jxsnh">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1897" data-mp="3inyif65ei0o2">
                      <option value="" data-id="element-1898" data-mp="34wjpa6hs4j3a">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1899" data-mp="2uy47wv8k0o6z">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1900" data-mp="2728zp4fa1tjg">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1901" data-mp="271r4mb4cpld4">
                      <option value="" data-id="element-1902" data-mp="2kte0wayamtvw">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1903" data-mp="2fukoymynnaxu">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1904" data-mp="14embm6wg22qt">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1905" data-mp="3g3ann4zhiaew">
                      <option value="" data-id="element-1906" data-mp="ckhcszy3bzof">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-1907" data-mp="2jda2rh28ke2v">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full bg-cta hover:bg-cta-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-1908" data-mp="8rr101c5mrkj">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1909" data-mp="3r39lek0k8ps" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* Undecided nudge — for visitors who can't fill the form yet */}
              <Link to="/degrees" className="group flex items-center gap-3 bg-light hover:bg-white border border-gray-200 rounded-lg px-4 py-3 transition-colors duration-200" data-id="element-1910" data-mp="33eut653him1b">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white group-hover:bg-light border border-gray-200 flex items-center justify-center text-navy transition-colors duration-200" data-id="element-1911" data-mp="2y2ec30uc9rsi">
                  <UsersIcon className="w-4 h-4" aria-hidden="true" data-id="element-1912" data-mp="2objnvqxj8i4f" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-1913" data-mp="3c1xhhlvrmp14">
                  <span className="block text-sm font-bold text-navy leading-tight" data-id="element-1914" data-mp="3gzp7gacuo12o">
                    Not sure what to study?
                  </span>
                  <span className="block text-xs text-gray-600 mt-0.5 leading-tight" data-id="element-1915" data-mp="321wvfs6ihut8">
                    Explore top programs by career
                  </span>
                </span>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-1916" data-mp="1qm86wwdm0nqb" />
              </Link>

              {/* What to Look For */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1917" data-mp="3ta6w3g9dafct">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1918" data-mp="3oj8ac15m0p26">
                  {guide.sidebarChecklistTitle}
                </h4>
                <ul className="space-y-2.5" data-id="element-1919" data-mp="3jjlx3rps78td">
                  {guide.sidebarChecklist.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-1920" data-mp="3emdg3ugddatp" data-mpi={i}>
                      <CheckCircleIcon className="w-4 h-4 text-crimson flex-shrink-0 mt-0.5" data-id="element-1921" data-mp="35jkm3jfsyl06" />
                      <span data-id="element-1922" data-mp="30lsxozed9h0q">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Related Links */}
              {guide.relatedLinks.length > 0 && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1923" data-mp="yuxvnptkgllk">
                  <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1924" data-mp="16e749gu90g9k">
                    Related
                  </h4>
                  <ul className="space-y-2" data-id="element-1925" data-mp="1bapa6k1beknh">
                    {guide.relatedLinks.map(link => <li key={link.href} data-id="element-1926" data-mp="1g5bk0irr055g">
                        <Link to={link.href} className="text-sm flex items-center text-gray-600 hover:text-crimson" data-id="element-1927" data-mp="1l2uwpe8ss9va">
                          <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-1928" data-mp="sd4g8yjp0vw0" />
                          {link.label}
                        </Link>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
        </div>
      </div>

      <div className="lg:hidden" data-id="element-1929" data-mp="28qunge64009v">
        <TrustSignals data-id="element-1930" data-mp="28rmlrwr3cc3u" />
      </div>

      {/* Guide Content Sections */}
      <div className="bg-white border-t border-gray-200 pt-16 pb-20" data-id="element-1931" data-mp="2dpo2ib11nzc5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1932" data-mp="2yui2pz2b70bc">
          {/* Overview */}
          <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-1933" data-mp="2tw88bqoes3qs">
            <h2 className="text-3xl font-bold text-navy mb-6" data-id="element-1934" data-mp="2ttvheo0oaj1l">
              {guide.overviewHeading}
            </h2>
            <div className="prose prose-lg text-gray-600 mx-auto leading-relaxed space-y-6 text-left" data-id="element-1935" data-mp="33u4gc9m1uiko">
              {guide.overviewParagraphs.map((p, i) => <p key={i} data-id="element-1936" data-mp="3hjy6qwd7dawi" data-mpi={i}>{p}</p>)}
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-20 bg-gray-50 rounded-2xl p-8 sm:p-12 border border-gray-200" data-id="element-1937" data-mp="3djo33qcvuwkt">
            <div className="text-center mb-12" data-id="element-1938" data-mp="3rixgyjdm9elm">
              <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-4" data-id="element-1939" data-mp="3mk41wmiolfjc">
                {guide.benefitsHeading}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1940" data-mp="1x6h2wwekww67">
                {guide.benefitsIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1941" data-mp="1s8xq80xj4rgd">
              {guide.benefits.map((benefit, i) => {
              const IconComponent = benefitIconMap[benefit.icon] || BookOpenIcon;
              return <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-1942" data-mp="3u3lpqc2ykrlp" data-mpi={i}>
                    <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1943" data-mp="3l2xbphdker8t">
                      <IconComponent className="w-6 h-6 text-navy" data-id="element-1944" data-mp="3q2texeefuenb" />
                    </div>
                    <h3 className="text-lg font-bold text-navy mb-2" data-id="element-1945" data-mp="3b5sorfbwarqy">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1946" data-mp="3g4vrgt4eklvr">
                      {benefit.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>

          {/* Careers */}
          <div className="mb-20" data-id="element-1947" data-mp="2hxf2as3q96to">
            <div className="text-center mb-10" data-id="element-1948" data-mp="2hwdinz8nbc0c">
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1949" data-mp="2mnvk0oekzelu">
                {guide.careersHeading}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1950" data-mp="28x0ftpf11lol">
                {guide.careersIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1951" data-mp="2dw3aqh1ambiu">
              {guide.careers.map((career, i) => <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-200 flex flex-col h-full" data-id="element-1952" data-mp="35mrbhrz21evz" data-mpi={i}>
                  <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center mb-4 text-navy" data-id="element-1953" data-mp="2wlt6p7gsl3cc">
                    <BriefcaseIcon className="w-6 h-6" data-id="element-1954" data-mp="1g7js352cqsyp" />
                  </div>
                  <h3 className="text-lg font-bold text-navy mb-2 leading-tight" data-id="element-1955" data-mp="1l6mpq4g05wwn">
                    {career.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-6 flex-grow" data-id="element-1956" data-mp="1dm289z70q4ao">
                    {career.desc}
                  </p>
                  <div className="pt-4 border-t border-gray-200 mt-auto" data-id="element-1957" data-mp="18qejvm0d1f68">
                    <div className="flex items-center text-sm font-semibold text-gray-800" data-id="element-1958" data-mp="1mnjnlfclq1yb">
                      <DollarSignIcon className="w-4 h-4 text-navy-light mr-1" data-id="element-1959" data-mp="1hqa0i6coq2dn" />
                      {career.salary}
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1960" data-mp="tx1qcuyk6ust">
                      Estimated Annual Salary
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* FAQs */}
          <div className="max-w-3xl mx-auto" data-id="element-1961" data-mp="yxfu40t4nil1">
            <div className="text-center mb-10" data-id="element-1962" data-mp="ywnywr3fzmva">
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1963" data-mp="b7v26sezxazm">
                {guide.faqHeading}
              </h2>
              <p className="text-gray-600" data-id="element-1964" data-mp="69a9de22ptl7">{guide.faqIntro}</p>
            </div>

            <div className="space-y-4" data-id="element-1965" data-mp="26b1e5fkdc7k3">
              {guide.faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-all duration-200" data-id="element-1966" data-mp="2ba4ambma95gg" data-mpi={index}>
                  <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-navy" onClick={() => toggleFaq(index)} aria-expanded={openFaqIndex === index} data-id="element-1967" data-mp="2l18t3lb638g1">
                    <span className="font-semibold text-navy pr-8" data-id="element-1968" data-mp="1xalhoq21m0oq">
                      {faq.q}
                    </span>
                    {openFaqIndex === index ? <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-1969" data-mp="38rbfbksehk6q" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-1970" data-mp="3cr3sybwnigiy" />}
                  </button>
                  {openFaqIndex === index && <div className="px-6 pb-5 pt-0" data-id="element-1971" data-mp="x2y5m3ri6hhs">
                      <p className="text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4 mt-2" data-id="element-1972" data-mp="x5avd33i5w5e">
                        {faq.a}
                      </p>
                    </div>}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </main>;
}